import { showToast } from '@crowdex/ui/components/toaster';
import type { WorkspacePort } from '@crowdex/workspace-protocol';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import {
  listPortForwards,
  openForwardedPort,
  type PortForward,
  startPortForward,
  stopPortForward,
} from '@/lib/cloud-runtime/port-forwards';
import { errorMessage } from '@/lib/error-message';
import { isLocalMachine } from '@/lib/local-runtime';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

const POLL_MS = 3_000;
/** Ports already suggested, once per conversation for this page. */
const suggested = new Set<string>();

export type SandboxPorts = {
  ports: readonly WorkspacePort[];
  /** This conversation's forwards, by sandbox port. */
  forwards: ReadonlyMap<number, PortForward>;
  /** Whether a local Crowdex server can forward ports to this machine. */
  canForward: boolean;
  forward(port: number, open?: boolean): Promise<void>;
  stop(port: number): Promise<void>;
};

/**
 * The ports a cloud conversation's sandbox listens on, polled while it is open, and their
 * forwards to this machine. A port that opens is suggested once (forward and open it).
 */
export function useSandboxPorts(input: {
  client: WorkspaceClient | null;
  cloudRuntime: CloudRuntime | null;
  conversation: string;
}): SandboxPorts {
  const { client, cloudRuntime, conversation } = input;
  const [ports, setPorts] = useState<readonly WorkspacePort[]>([]);
  const [forwards, setForwards] = useState<ReadonlyMap<number, PortForward>>(new Map());
  const canForward = cloudRuntime !== null && isLocalMachine();

  const refreshForwards = useCallback(async () => {
    if (!canForward) return;
    const all = await listPortForwards();
    setForwards(
      new Map(
        all
          .filter((forward) => forward.conversation === conversation)
          .map((forward) => [forward.remotePort, forward]),
      ),
    );
  }, [canForward, conversation]);

  const forward = useCallback(
    async (port: number, open = false) => {
      if (cloudRuntime === null) return;
      try {
        const started = await startPortForward(cloudRuntime, conversation, port);
        await refreshForwards();
        if (open) openForwardedPort(started.localPort);
      } catch (error) {
        showToast({
          title: `Couldn’t forward port ${port}`,
          description: errorMessage(error),
          tone: 'error',
        });
      }
    },
    [cloudRuntime, conversation, refreshForwards],
  );
  const forwardRef = useRef(forward);
  forwardRef.current = forward;

  const stop = useCallback(
    async (port: number) => {
      await stopPortForward(conversation, port).catch(() => undefined);
      await refreshForwards();
    },
    [conversation, refreshForwards],
  );

  useEffect(() => {
    void refreshForwards().catch(() => undefined);
  }, [refreshForwards]);

  // A sandbox that restarted has a new token: its forwards are renewed with it (when the runtime
  // changes, not when a forward is added).
  const forwardsRef = useRef(forwards);
  forwardsRef.current = forwards;
  useEffect(() => {
    if (cloudRuntime === null || !canForward) return;
    for (const port of forwardsRef.current.keys())
      void startPortForward(cloudRuntime, conversation, port).catch(() => undefined);
  }, [cloudRuntime, canForward, conversation]);

  useEffect(() => {
    setPorts([]);
    if (client === null || cloudRuntime === null) return;
    let disposed = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      const listed = await client.listPorts().catch(() => null);
      if (disposed) return;
      if (listed !== null) {
        setPorts((current) =>
          JSON.stringify(current) === JSON.stringify(listed) ? current : listed,
        );
        if (isLocalMachine())
          for (const port of listed) {
            const id = `${conversation}:${port.port}`;
            if (suggested.has(id)) continue;
            suggested.add(id);
            showToast({
              title: `Port ${port.port} is open in the cloud sandbox`,
              description: `${port.process ?? 'A process'} listens on it. Forward it to localhost:${port.port} to use your own browser.`,
              tone: 'info',
              duration: 15_000,
              action: {
                label: 'Forward and open',
                onSelect: () => void forwardRef.current(port.port, true),
              },
            });
          }
      }
      timer = setTimeout(() => void poll(), POLL_MS);
    };
    void poll();
    return () => {
      disposed = true;
      clearTimeout(timer);
    };
  }, [client, cloudRuntime, conversation]);

  return { ports, forwards, canForward, forward, stop };
}
